import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LeadRow, LeadStatus } from '@/api/m3-types';
import { useLeadBoard, useLeadList } from '@/api/m3';
import { Board, BoardColumn, BoardSkeleton, DealCard } from '@/components/kit/Board';
import { Button } from '@/components/kit/Button';
import { TextLink } from '@/components/kit/Controls';
import { DateRangePicker } from '@/components/kit/DateRangePicker';
import { FilterBar, FilterChip, SearchInput } from '@/components/kit/Filters';
import { Segments } from '@/components/kit/Layout';
import { MetricRow } from '@/components/kit/MetricRow';
import { ErrorState } from '@/components/kit/Note';
import { Avatar, Who } from '@/components/kit/Person';
import { Pill } from '@/components/kit/Pill';
import { formatPhone } from '@/lib/phone';
import { useDebouncedSearch } from '@/features/base/shared/useDebouncedSearch';
import { ListPanel } from '@/features/base/shared/ListPanel';
import { useListParams } from '@/features/base/shared/useListParams';
import { CrmHeader, SectionBar, type ViewMode } from '../CrmHeader';
import { dateTime, deals, initialsOf, LEAD_STATUS, minutesText, shortUser } from '../labels';
import { NewLeadModal } from './LeadDialogs';

const SEGMENTS: { value: LeadStatus | 'all'; label: string }[] = [
  { value: 'new', label: 'Новые' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'converted', label: 'Сконвертированы' },
  { value: 'rejected', label: 'Отказы' },
  { value: 'duplicate', label: 'Дубли' },
  { value: 'all', label: 'Все' },
];

/** Касание: не взята — «ждёт …», красным за порогом (touchCell в макете) */
export function TouchCell({ lead }: { lead: LeadRow }) {
  if (lead.touchMinutes === null) return <span className="na">—</span>;
  if (lead.waiting) {
    return (
      <>
        <div className={lead.overThreshold ? 'ctx' : 'wtx'}>не взята</div>
        <div className={lead.overThreshold ? 'sub od' : 'sub'}>ждёт {minutesText(lead.touchMinutes)}</div>
      </>
    );
  }
  return <div className={lead.overThreshold ? 'num ctx' : 'num'}>{minutesText(lead.touchMinutes)}</div>;
}

function StatusCell({ lead }: { lead: LeadRow }) {
  const s = LEAD_STATUS[lead.status];
  const sub =
    lead.status === 'rejected'
      ? lead.rejectReason
      : lead.status === 'duplicate'
        ? lead.duplicateOf
          ? `дубль #${lead.duplicateOf}`
          : null
        : lead.status === 'converted'
          ? lead.dealsCount > 1
            ? deals(lead.dealsCount)
            : 'в сделку'
          : null;
  return (
    <>
      <Pill tone={s.tone}>{s.label}</Pill>
      {sub ? <div className="sub">{sub}</div> : null}
    </>
  );
}

export function OwnerChip({ owner, none = 'ничья' }: { owner: { fullName: string } | null; none?: string }) {
  if (!owner) return <span className="dim">{none}</span>;
  return (
    <span className="cwho">
      <Avatar initials={initialsOf(owner.fullName)} size={22} />
      <span className="nw">{shortUser(owner.fullName)}</span>
    </span>
  );
}

/**
 * Заявки (viewLeads в макете, БЛ 11.2): сегменты статусов, фильтры,
 * срез цифр — новые, ждут дольше порога, конверсия, медиана касания.
 * Доска — те же заявки колонками «Новые · В работе · Обработаны».
 */
export function LeadsPage() {
  const navigate = useNavigate();
  const { values, page, set } = useListParams(['status', 'sourceId', 'owner', 'from', 'to', 'q', 'view'] as const);
  const mode: ViewMode = values.view === 'board' ? 'board' : 'list';
  const status = (values.status as LeadStatus | 'all' | null) ?? 'new';
  const [text, setText] = useDebouncedSearch(values.q, (q) => set({ q }));
  const [creating, setCreating] = useState(false);

  const filters = { sourceId: values.sourceId, owner: values.owner, from: values.from, to: values.to, q: values.q };
  const list = useLeadList({ ...filters, status, page });
  const board = useLeadBoard(filters, mode === 'board');
  const data = list.data;
  const filtered = Boolean(values.sourceId || values.owner || values.from || values.q);
  const open = (id: number) => navigate(`/crm/leads/${id}`);

  const head = (
    <tr>
      <th className="c-lead srt">
        Заявка <span className="ar">▼</span>
      </th>
      <th className="c-ch">Канал и метка</th>
      <th className="c-touch">Касание</th>
      <th className="c-lst">Статус</th>
      <th className="c-lev">Мероприятие</th>
      <th className="c-lown">Ответственный</th>
      <th className="r">Поступила</th>
    </tr>
  );

  const reset = () => set({ sourceId: null, owner: null, from: null, to: null, q: null });

  return (
    <div className="main">
      <CrmHeader
        current="/crm/leads"
        action={
          <Button tone="primary" onClick={() => setCreating(true)}>
            ＋ Заявка
          </Button>
        }
      />

      <SectionBar
        mode={mode}
        onMode={(m) => set({ view: m === 'board' ? 'board' : null })}
        left={
          mode === 'list' ? (
            <Segments
              value={status}
              onChange={(s) => set({ status: s === 'new' ? null : s })}
              items={SEGMENTS.map((s) => ({ ...s, count: data?.counts[s.value] }))}
            />
          ) : undefined
        }
      />

      <FilterBar search={<SearchInput value={text} onChange={setText} placeholder="Имя, телефон, номер заявки" />}>
        <FilterChip
          label="Канал"
          value={values.sourceId}
          onChange={(sourceId) => set({ sourceId })}
          options={(data?.facets.sources ?? []).map((f) => ({ value: f.value, label: f.label, side: f.count }))}
        />
        <FilterChip
          label="Ответственный"
          value={values.owner}
          onChange={(owner) => set({ owner })}
          options={(data?.facets.owners ?? []).map((f) => ({ value: f.value, label: f.label, side: f.count }))}
        />
        <DateRangePicker label="Поступила" from={values.from} to={values.to} onChange={(from, to) => set({ from, to })} />
      </FilterBar>

      {data ? (
        <MetricRow
          variant="slice"
          items={[
            { label: 'Новых', value: data.metrics.new },
            {
              label: `Ждут дольше ${data.touchMinutes} мин`,
              value: data.metrics.waitingOverThreshold,
              over: data.metrics.waitingOverThreshold > 0,
            },
            { label: 'Сконвертировано', value: data.metrics.converted, sub: `из ${data.metrics.total} заявок` },
            {
              label: 'Медиана до касания',
              value: data.metrics.medianTouchMinutes === null ? '—' : minutesText(data.metrics.medianTouchMinutes),
            },
          ]}
        />
      ) : null}

      {mode === 'board' ? (
        board.error ? (
          <ErrorState title="Не удалось загрузить доску." message={board.error.message} onRetry={() => void board.refetch()} />
        ) : board.isLoading || !board.data ? (
          <BoardSkeleton columns={3} />
        ) : (
          <Board columns={3}>
            {board.data.columns.map((col) => (
              <BoardColumn
                key={col.key}
                title={col.key === 'new' ? 'Новые — взять в работу' : col.key === 'in_progress' ? 'В работе' : 'Обработаны'}
                dot={col.key === 'new' ? 'info' : col.key === 'in_progress' ? 'warn' : 'neutral'}
                count={col.count}
              >
                {col.items.length
                  ? col.items.map((l) => (
                      <DealCard
                        key={l.id}
                        id={String(l.id)}
                        title={l.title}
                        onOpen={() => open(l.id)}
                        meta={
                          <>
                            #{l.id} · {l.source.name}
                            {l.event ? ` · ${l.event.city}` : ''}
                            {l.message ? (
                              <>
                                <br />
                                {l.message.length > 70 ? `${l.message.slice(0, 70)}…` : l.message}
                              </>
                            ) : null}
                          </>
                        }
                        footer={
                          <>
                            {l.waiting ? (
                              <span className={l.overThreshold ? 'ctx' : 'wtx'}>ждёт {minutesText(l.touchMinutes ?? 0)}</span>
                            ) : (
                              <Pill tone={LEAD_STATUS[l.status].tone}>{LEAD_STATUS[l.status].label}</Pill>
                            )}
                            <span className="sp" />
                            {l.owner ? <Avatar initials={initialsOf(l.owner.fullName)} size={22} /> : null}
                          </>
                        }
                      />
                    ))
                  : undefined}
              </BoardColumn>
            ))}
          </Board>
        )
      ) : (
        <ListPanel
          head={head}
          cols={7}
          loading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          errorTitle="Не удалось загрузить заявки."
          empty={
            data && !data.items.length ? (
              filtered ? (
                <>
                  По этим условиям заявок нет.
                  <br />
                  <TextLink onClick={reset}>Сбросить фильтры</TextLink>
                </>
              ) : (
                <>Заявок в этом статусе нет.</>
              )
            ) : null
          }
          paging={
            data
              ? { page, pageSize: data.pageSize, total: data.total, shown: data.items.length, onPage: (p) => set({ page: String(p) }) }
              : undefined
          }
          rows={data?.items.map((l) => {
            const at = dateTime(l.createdAt);
            return (
              <tr key={l.id} className="clk" onClick={() => open(l.id)}>
                <td data-col="primary">
                  <Who
                    initials={l.initials}
                    name={l.title}
                    sub={
                      <div className="sub">
                        #{l.id} · <span className="num">{formatPhone(l.phone)}</span>
                      </div>
                    }
                  />
                </td>
                <td>
                  <div>{l.source.name}</div>
                  <div className="sub">{l.campaign ?? (l.isMini ? 'мини-заявка менеджера' : 'без меток')}</div>
                </td>
                <td>
                  <TouchCell lead={l} />
                </td>
                <td>
                  <StatusCell lead={l} />
                </td>
                <td className="muted">{l.event?.label ?? <span className="dim">не указано</span>}</td>
                <td>
                  <OwnerChip owner={l.owner} />
                </td>
                <td className="r" data-col="secondary">
                  <div className="num">{at.date}</div>
                  <div className="sub">{at.time}</div>
                </td>
              </tr>
            );
          })}
        />
      )}

      {creating ? <NewLeadModal onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

